import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FiGrid, FiYoutube, FiTwitter, FiUser, FiBell, FiMenu, FiX, FiMessageCircle } from 'react-icons/fi';
import { FaDiscord, FaFacebookF } from 'react-icons/fa';
import { theme } from '../theme';
import { social, subscribeUrl } from '../config/social';
import { t, locale, setLocale } from '../config/i18n';
import { useAuth } from '../context/AuthContext';
import { usePresence } from '../context/PresenceContext';
import AuthModal from './AuthModal';
import DrawerProfile from './DrawerProfile';

const OnlineBadge = styled.div`
  display: inline-flex; align-items: center; gap: .55rem; margin-right: .3rem;
  .chip { display: inline-flex; align-items: center; gap: .35rem; font-size: .78rem; font-weight: 700;
    padding: .3rem .7rem; border-radius: 999px; border: 1px solid ${theme.colors.border};
    background: rgba(255,255,255,.04); color: ${theme.colors.textSecondary}; white-space: nowrap; }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 8px #22c55e; }
  .friends { color: ${theme.colors.primaryLight}; border-color: ${theme.colors.borderHover}; background: rgba(124,58,237,.12); }
  @media (max-width: 720px) { .chip.hide-sm { display: none; } }
`;

function OnlinePresence() {
  const { total, friendsTotal, min } = usePresence();
  const { isLoggedIn } = useAuth();
  const showTotal = total >= min;          // total masqué sous le seuil (évite l'effet « vide »)
  const showFriends = isLoggedIn;          // amis en ligne : toujours affiché quand connecté (même 0)
  if (!showTotal && !showFriends) return null;
  return (
    <OnlineBadge>
      {showTotal && <span className="chip hide-sm" title="Joueurs connectés à l'écosystème VertinGo"><span className="dot" /> {total} en ligne</span>}
      {showFriends && <span className="chip friends" title="Tes amis actuellement en ligne">👥 {friendsTotal || 0} ami{(friendsTotal || 0) > 1 ? 's' : ''} en ligne</span>}
    </OnlineBadge>
  );
}

const slideDown = keyframes`
  from { transform: translateY(-100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  @media (max-width: 1240px) { padding: 0 1rem; }
  background: ${({ scrolled }) =>
    scrolled
      ? 'rgba(13,13,26,0.95)'
      : 'rgba(13,13,26,0.6)'};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid ${({ scrolled }) =>
    scrolled ? theme.colors.border : 'transparent'};
  transition: ${theme.transitions.default};
  animation: ${slideDown} 0.4s ease;
`;

const Brand = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-family: ${theme.fonts.heading};
  font-size: 1.3rem;
  font-weight: 700;
  color: ${theme.colors.textPrimary};

  svg {
    color: ${theme.colors.primary};
    font-size: 1.5rem;
  }

  span.highlight {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

// 🔑 LE SEUIL EST 1240 px PARCE QUE LA BARRE EN A BESOIN D'AUTANT, pas parce
// que 1200 est un chiffre rond. Mesuré en appareil virtuel le 19/10/2026 : à
// 1024 px — la largeur de TOUS les iPad — le seuil précédent (980 px) faisait
// afficher la navigation complète, qui réclamait 1176 px. Résultat visible :
// « Support » coupé au bord droit, le hamburger ET la barre complète affichés
// en même temps, et le logo « VertinGo » superposé au lien « Home ».
// ⚠️ Ce seuil dépend du NOMBRE d'entrées de menu. En ajouter une sans
// re-mesurer ramène le défaut : relancer
//   local-tools/test_appareils.py --url https://store.vertingo.eu/ --classes Tab-L
//
// 🔑 RELEVÉ DU 30/10/2026 — l'entrée « Palettes » a été ajoutée, donc le seuil
// est passé de 1200 à 1340 px. L'avertissement ci-dessus s'appliquait
// exactement à ce cas. Deux précautions prises :
//  1. la barre du bureau affiche le libellé COURT (`nav.palettesCourt`,
//     « 🎬 Palettes ») ; le tiroir mobile garde « 🎬 Crée ta palette », qui
//     n'a aucune contrainte de largeur ;
//  2. les QUATRE media queries de la barre ont été remontées ensemble. Les
//     désaligner laisserait une bande (1200-1340 px) SANS barre complète ET
//     SANS hamburger, c'est-à-dire sans aucune navigation.
// ⚠️ 1340 est une ESTIMATION (87 caractères de libellés réclamaient 1176 px,
// soit ~13,5 px/caractère ; le libellé court en ajoute 11). À confirmer par le
// relevé en appareil virtuel au prochain déploiement.
const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  @media (max-width: 1240px) { display: none; }
`;

// ── Menu mobile (drawer) ────────────────────────────────────────────────────
const Hamburger = styled.button`
  display: none;
  align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: ${theme.radii.full};
  color: ${theme.colors.textPrimary}; background: rgba(255,255,255,0.06);
  border: 1px solid ${theme.colors.border}; cursor: pointer; font-size: 1.35rem;
  @media (max-width: 1240px) { display: inline-flex; }
`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1099;
  background: rgba(0,0,0,0.55); backdrop-filter: blur(2px);
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  transition: opacity .25s ease;
`;

const Drawer = styled.aside`
  position: fixed; top: 0; left: 0; z-index: 1100;
  height: 100%; width: min(82vw, 320px);
  display: flex; flex-direction: column; gap: .2rem;
  padding: 5rem 1rem 1.5rem;
  background: rgba(13,13,26,0.98);
  border-right: 1px solid ${theme.colors.border};
  box-shadow: 12px 0 40px rgba(0,0,0,0.5);
  transform: translateX(${({ open }) => (open ? '0' : '-100%')});
  transition: transform .28s cubic-bezier(.4,0,.2,1);
  overflow-y: auto;

  a {
    padding: .85rem 1rem; border-radius: ${theme.radii.md || '12px'};
    font-size: 1rem; font-weight: 600; text-decoration: none;
    color: ${theme.colors.textSecondary};
  }
  a.active { color: ${theme.colors.primaryLight}; background: rgba(124,58,237,0.18); }
  a:hover { color: ${theme.colors.textPrimary}; background: rgba(124,58,237,0.12); }
  .drawer-social { display: flex; gap: .6rem; margin-top: 1rem; padding: 0 .4rem; }
  /* Bouton « Chat communauté » (dans le menu, mobile) — même look que les liens de nav. */
  button.chat-entry {
    display: flex; align-items: center; gap: .6rem; width: 100%; text-align: left;
    padding: .85rem 1rem; border: none; background: none; cursor: pointer;
    border-radius: ${theme.radii.md || '12px'}; font-size: 1rem; font-weight: 600;
    color: ${theme.colors.textSecondary}; font-family: inherit;
  }
  button.chat-entry:hover { color: ${theme.colors.textPrimary}; background: rgba(124,58,237,0.12); }
`;

const CloseBtn = styled.button`
  position: absolute; top: 1rem; right: 1rem;
  width: 38px; height: 38px; border-radius: ${theme.radii.full};
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.06); border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textPrimary}; cursor: pointer; font-size: 1.3rem;
`;

const NavItem = styled(NavLink)`
  position: relative;
  /* 🔑 POLICE ADAPTATIVE (30/10/2026). Le user : « le menu toolbar commence à
     être vraiment serré […] on pourrait peut-être réduire la taille de la
     police pour mieux l'adapter ». Un clamp() vaut mieux qu'un palier : la
     barre se resserre PROGRESSIVEMENT au lieu de basculer d'un coup en
     hamburger. 0,9rem au-delà de ~1900px, 0,78rem vers 1280px. */
  padding: 0.4rem 0.62rem;
  border-radius: ${theme.radii.full};
  font-size: clamp(0.78rem, 0.62vw + 0.42rem, 0.9rem);
  white-space: nowrap;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.textPrimary};
    background: rgba(124,58,237,0.12);
  }

  &.active {
    color: ${theme.colors.primaryLight};
    background: rgba(124,58,237,0.18);
  }
`;

// Item Premium mis en avant (doré) dans la nav.
const PremiumItem = styled(NavItem)`
  color: #ffd36a;
  font-weight: 700;
  &:hover { color: #fff; background: linear-gradient(135deg, rgba(255,180,60,0.25), rgba(124,58,237,0.25)); }
  &.active { color: #111; background: linear-gradient(135deg, #ffd36a, #f7a83c); }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* Sur mobile, les icônes réseaux (liens directs) passent dans le drawer → on
     ne garde que le compte pour désencombrer la barre. AccountControl est un
     <div>, donc épargné par ce sélecteur d'enfants directs <a>. */
  @media (max-width: 1240px) { & > a { display: none; } }
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: ${theme.radii.full};
  color: ${theme.colors.textMuted};
  background: rgba(255,255,255,0.04);
  border: 1px solid ${theme.colors.border};
  transition: ${theme.transitions.default};
  text-decoration: none;
  font-size: 1rem;

  &:hover {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.borderHover};
    background: rgba(124,58,237,0.12);
    box-shadow: ${theme.shadows.glow};
  }
`;

const AccountCtl = styled.div`display: flex; align-items: center; gap: 0.4rem;`;
const IconBtn = styled.button`
  position: relative; display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: ${theme.radii.full};
  color: ${theme.colors.textMuted}; background: rgba(255,255,255,0.04);
  border: 1px solid ${theme.colors.border}; cursor: pointer; font-size: 1rem;
  transition: ${theme.transitions.default}; text-decoration: none;
  &:hover { color: ${theme.colors.primary}; border-color: ${theme.colors.borderHover}; background: rgba(124,58,237,0.12); }
  .dot { position: absolute; top: -3px; right: -3px; min-width: 15px; height: 15px; padding: 0 3px;
    border-radius: 999px; background: ${theme.colors.primary}; color: #fff; font-size: .62rem; font-weight: 700;
    display: flex; align-items: center; justify-content: center; }`;
const LoginBtn = styled.button`
  display: inline-flex; align-items: center; gap: .4rem; border: none; cursor: pointer;
  background: ${theme.colors.gradient}; color: #fff; padding: .45rem .9rem; border-radius: ${theme.radii.full};
  font-weight: 700; font-size: .82rem;`;
// Accès explicite à l'espace membre (avatar + libellé « Mon espace ») — plus évident qu'un simple rond.
const AccountBtn = styled(NavLink)`
  display: inline-flex; align-items: center; gap: .45rem; text-decoration: none;
  background: rgba(124,58,237,0.14); border: 1px solid ${theme.colors.borderHover};
  color: ${theme.colors.textPrimary}; padding: .28rem .8rem .28rem .32rem; border-radius: ${theme.radii.full};
  font-weight: 700; font-size: .8rem; transition: ${theme.transitions.default}; white-space: nowrap;
  &:hover { background: rgba(124,58,237,0.26); border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.glow}; }
  .ava { width: 26px; height: 26px; border-radius: 50%; background: ${theme.colors.gradient};
    display: flex; align-items: center; justify-content: center; color: #fff; font-size: .68rem; font-weight: 700; flex-shrink: 0; }
  @media (max-width: 560px) { .lbl { display: none; } padding: .28rem; }`;

function AccountControl() {
  const { enabled, isLoggedIn, user, notifCount } = useAuth();
  const [modal, setModal] = useState(false);
  if (!enabled) return null;
  const initials = ((user && (user.display_name || user.username)) || '?').slice(0, 2).toUpperCase();
  return (
    <AccountCtl>
      {/* Bascule de langue FR/EN — bien visible (l'audience internationale peut passer en anglais). */}
      <button onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')} title={locale === 'fr' ? 'Switch to English' : 'Passer en français'}
        style={{ background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.textSecondary,
          borderRadius: 999, padding: '.2rem .5rem', cursor: 'pointer', fontSize: '.72rem', fontWeight: 800, letterSpacing: '.03em', flexShrink: 0 }}>
        {locale === 'fr' ? '🇫🇷 FR' : '🇬🇧 EN'}
      </button>
      {isLoggedIn ? (
        <>
          <IconBtn as={NavLink} to="/compte?tab=notifs" title="Notifications"
            onClick={() => { try { window.dispatchEvent(new CustomEvent('vg-open-notifs')); } catch (e) { /* */ } }}>
            <FiBell />{notifCount > 0 && <span className="dot">{notifCount > 9 ? '9+' : notifCount}</span>}
          </IconBtn>
          <AccountBtn to="/compte" title="Mon espace membre">
            <span className="ava">{initials !== '?' ? initials : <FiUser />}</span>
            <span className="lbl">{t('nav.account')}</span>
          </AccountBtn>
        </>
      ) : (
        <LoginBtn onClick={() => setModal(true)}><FiUser /> {t('nav.login')}</LoginBtn>
      )}
      {modal && <AuthModal onClose={() => setModal(false)} />}
    </AccountCtl>
  );
}

const NAV = [
  { to: '/', label: t('nav.home'), end: true },
  { to: '/store', label: t('nav.games') },
  { to: '/videos', label: t('nav.videos') },
  { to: '/channels', label: t('nav.channels') },
  { to: '/palettes', label: t('nav.palettes') },
  { to: '/communaute', label: t('nav.community') },
  { to: '/classements', label: t('nav.leaderboards') },
  { to: '/salons', label: t('nav.online') },
  { to: '/embed', label: t('nav.embed') },
  { to: '/about', label: t('nav.about') },
  { to: '/support', label: t('nav.support') },
  { to: '/premium', label: t('nav.premium') },
];

export default function Toolbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Verrouille le scroll du body quand le drawer est ouvert.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
    <Nav scrolled={scrolled ? 1 : 0}>
      <Hamburger onClick={() => setOpen(true)} aria-label="Ouvrir le menu">
        <FiMenu />
      </Hamburger>

      <Brand to="/">
        <FiGrid />
        Vertin<span className="highlight">Go</span>
      </Brand>

      <NavLinks>
        <NavItem to="/" end>{t('nav.home')}</NavItem>
        <NavItem to="/store">{t('nav.games')}</NavItem>
        <NavItem to="/videos">{t('nav.videos')}</NavItem>
        <NavItem to="/channels">{t('nav.channels')}</NavItem>
        <NavItem to="/palettes">{t('nav.palettesCourt')}</NavItem>
        <NavItem to="/communaute">{t('nav.community')}</NavItem>
        <NavItem to="/classements">{t('nav.leaderboards')}</NavItem>
        <NavItem to="/salons">{t('nav.online')}</NavItem>
        <NavItem to="/embed">{t('nav.embed')}</NavItem>
        <NavItem to="/about">{t('nav.about')}</NavItem>
        <NavItem to="/support">{t('nav.support')}</NavItem>
        <PremiumItem to="/premium">{t('nav.premium')}</PremiumItem>
      </NavLinks>

      <SocialLinks>
        {/* Compteurs en ligne DÉPLACÉS dans une section dédiée (page En ligne + drawer) → on ne
            surcharge plus la toolbar (qui tronquait « Mon espace »). */}
        <SocialIcon href={subscribeUrl()} target="_blank" rel="noreferrer" aria-label="YouTube">
          <FiYoutube />
        </SocialIcon>
        {social.facebook && (
          <SocialIcon href={social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook">
            <FaFacebookF />
          </SocialIcon>
        )}
        {social.discord && (
          <SocialIcon href={social.discord} target="_blank" rel="noreferrer" aria-label="Discord">
            <FaDiscord />
          </SocialIcon>
        )}
        {social.twitter && (
          <SocialIcon href={social.twitter} target="_blank" rel="noreferrer" aria-label="Twitter / X">
            <FiTwitter />
          </SocialIcon>
        )}
        <AccountControl />
      </SocialLinks>
    </Nav>

    <Overlay open={open} onClick={() => setOpen(false)} />
    <Drawer open={open} role="dialog" aria-label="Menu de navigation">
      <CloseBtn onClick={() => setOpen(false)} aria-label="Fermer le menu"><FiX /></CloseBtn>
      <DrawerProfile onNavigate={() => setOpen(false)} />
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? 'active' : '')} onClick={() => setOpen(false)}>
          {n.label}
        </NavLink>
      ))}
      {/* Chat communauté — accessible depuis le menu (sur mobile la languette flottante est masquée). */}
      <button type="button" className="chat-entry" onClick={() => { setOpen(false); try { window.dispatchEvent(new CustomEvent('vg-open-chat')); } catch (e) { /* */ } }}>
        <FiMessageCircle /> Chat communauté
      </button>
      <div className="drawer-social">
        <SocialIcon href={subscribeUrl()} target="_blank" rel="noreferrer" aria-label="YouTube"><FiYoutube /></SocialIcon>
        {social.facebook && <SocialIcon href={social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><FaFacebookF /></SocialIcon>}
        {social.discord && <SocialIcon href={social.discord} target="_blank" rel="noreferrer" aria-label="Discord"><FaDiscord /></SocialIcon>}
        {social.twitter && <SocialIcon href={social.twitter} target="_blank" rel="noreferrer" aria-label="Twitter / X"><FiTwitter /></SocialIcon>}
      </div>
    </Drawer>
    </>
  );
}
